/* import __COLOCATED_TEMPLATE__ from './sidebar.hbs'; */
/* RESPONSIBLE TEAM: team-workflows */
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { request } from 'embercom/lib/inbox/requests';
import type Session from 'embercom/services/session';

interface Args {
  conversationId: number;
}

interface WorkflowTableRow {
  workflowName: string;
  matched: boolean;
  executed: boolean;
}

interface TriggerDebuggingInfo {
  trigger_class: string;
  trigger_instances: [
    {
      timestamp: Date;
      rulesets: [
        {
          id: number;
          title: string;
          matched: boolean;
          executed: boolean;
          workflow_type: string;
        },
      ];
    },
  ];
}

interface Signature {
  Args: Args;
}

export default class WorkflowsSidebar extends Component<Signature> {
  @service declare session: Session;

  @tracked workflowDebuggingInfo: TriggerDebuggingInfo[] = [];
  @tracked selectedTrigger: $TSFixMe;
  @tracked fetchingWorkflows = false;

  constructor(owner: unknown, args: Args) {
    super(owner, args);

    this.fetchWorkflowDebuggingInfo();
  }

  get workflowTriggers() {
    return this.workflowDebuggingInfo.map((trigger: TriggerDebuggingInfo) => ({
      id: trigger.trigger_class,
      text: trigger.trigger_class,
    }));
  }

  get workflowTableData(): WorkflowTableRow[] {
    if (!this.selectedTrigger) {
      return [];
    }

    let trigger = this.workflowDebuggingInfo.find(
      (trigger) => trigger.trigger_class === this.selectedTrigger.id,
    );

    if (!trigger) {
      return [];
    }

    //flat map the workflows in each trigger instance
    return trigger.trigger_instances.flatMap((instance) =>
      instance.rulesets.map((ruleset) => ({
        workflowName: ruleset.title,
        matched: ruleset.matched,
        executed: ruleset.executed,
      })),
    );
  }

  get triggerInstanceTables() {
    if (!this.selectedTrigger) {
      return [];
    }

    let trigger = this.workflowDebuggingInfo.find(
      (trigger) => trigger.trigger_class === this.selectedTrigger.id,
    );

    if (!trigger) {
      return [];
    }

    return trigger.trigger_instances
      .map((instance) => ({
        timestamp: instance.timestamp,
        workflows: instance.rulesets.map((ruleset) => ({
          workflowName: ruleset.title,
          matched: ruleset.matched,
          executed: ruleset.executed,
        })),
      }))
      .filter((instanceTable) => instanceTable.workflows.length > 0);
  }

  @action
  onTriggerSelect(trigger: { id: string; text: string }) {
    this.selectedTrigger = trigger;
  }

  private async fetchWorkflowDebuggingInfo() {
    this.fetchingWorkflows = true;
    let response = await request(
      `/ember/conversations/${this.args.conversationId}/debug_workflows?app_id=${this.session.workspace.id}`,
    );

    this.workflowDebuggingInfo = (await response.json()).triggers;
    this.fetchingWorkflows = false;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::Workflows::Sidebar': typeof WorkflowsSidebar;
    'inbox2/workflows/sidebar': typeof WorkflowsSidebar;
  }
}
